import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface BankAccountBalance {
  id: string;
  bank_account_id: string;
  balance_date: string;
  opening_balance: number | null;
  closing_balance: number | null;
  currency: string;
  created_at: string;
}

export interface BankAccountWithBalance {
  id: string;
  name: string;
  bank_name: string | null;
  account_number: string | null;
  account_type: string | null;
  currency: string | null;
  is_active: boolean | null;
  current_balance: number | null;
  balance_updated_at: string | null;
  statement_format: string | null;
}

export const useBankAccountsWithBalances = () => {
  return useQuery({
    queryKey: ['bank-accounts-with-balances'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      return data as BankAccountWithBalance[];
    },
  });
};

export const useBankAccountBalanceHistory = (bankAccountId: string | undefined) => {
  return useQuery({
    queryKey: ['bank-account-balances', bankAccountId],
    queryFn: async () => {
      if (!bankAccountId) return [];
      
      const { data, error } = await supabase
        .from('bank_account_balances')
        .select('*')
        .eq('bank_account_id', bankAccountId)
        .order('balance_date', { ascending: false })
        .limit(90);
      
      if (error) throw error;
      return data as BankAccountBalance[];
    },
    enabled: !!bankAccountId,
  });
};

export const useUpdateBankAccountBalance = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ bankAccountId, balance }: { bankAccountId: string; balance: number }) => {
      const { data, error } = await supabase
        .from('bank_accounts')
        .update({
          current_balance: balance,
          balance_updated_at: new Date().toISOString(),
        })
        .eq('id', bankAccountId)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      queryClient.invalidateQueries({ queryKey: ['bank-accounts-with-balances'] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to update balance: ${error.message}`);
    },
  });
};

export const useUpsertDailyBalance = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({
      bankAccountId,
      date,
      closingBalance,
      currency,
    }: {
      bankAccountId: string;
      date: string;
      closingBalance: number;
      currency: string;
    }) => {
      const { data, error } = await supabase
        .from('bank_account_balances')
        .upsert(
          {
            bank_account_id: bankAccountId,
            balance_date: date,
            closing_balance: closingBalance,
            currency,
          },
          { onConflict: 'bank_account_id,balance_date' }
        )
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-account-balances'] });
    },
  });
};
